// Hint definitions for the HintProvider system
// Each hint has an id, title, message, and optional configuration

export type HintDefinition = {
  id: string;
  title: string;
  message: string;
  // Optional: which pages/screens this hint applies to
  pages?: string[];
  // Optional: priority for display order (lower = higher priority)
  priority?: number;
};

export const HINTS: Record<string, HintDefinition> = {
  // Table/Drawer hints
  TABLE_DRAWER_FILTERS: {
    id: "table_drawer_filters",
    title: "Filter Your Data",
    message:
      "Use filters to narrow down the results in your table. Combine multiple filters for more specific searches.",
    pages: ["projects", "clients", "invoices", "timecards", "shifts"],
    priority: 1,
  },
  TABLE_DRAWER_SAVED_SEARCHES: {
    id: "table_drawer_saved_searches",
    title: "Save Your Searches",
    message:
      "Click the bookmark icon to save your current filter and sort configuration. Access saved searches anytime from the dropdown.",
    pages: ["projects", "clients", "invoices", "timecards", "shifts"],
    priority: 2,
  },

  // Kanban hints
  KANBAN_HIDDEN_CARDS: {
    id: "kanban_hidden_cards",
    title: "Hidden Cards",
    message: "Cards hidden by filters are shown at the bottom of each lane. Adjust your filters to see all cards.",
    pages: ["schedules"],
    priority: 1,
  },
  KANBAN_MULTI_DAY_SHIFTS: {
    id: "kanban_multi_day_shifts",
    title: "Multi-Day Shifts",
    message:
      "Shifts spanning multiple days can be displayed on their start day only or across all days. Toggle this in the view settings.",
    pages: ["schedules"],
    priority: 2,
  },

  // Shift hints
  SHIFT_STATUS: {
    id: "shift_status",
    title: "Shift Status",
    message: "Track shift progress with status: Pending, Approved, Advanced, Canceled, or Canceled - Billable.",
    pages: ["shifts", "schedules"],
    priority: 3,
  },

  // Project hints
  PROJECT_TERMS: {
    id: "project_terms",
    title: "Project Terms",
    message:
      "Project terms define billing and payroll rates. These can be inherited from the client or set specifically for each project.",
    pages: ["projects"],
    priority: 1,
  },

  // General navigation hints
  KEYBOARD_SHORTCUTS: {
    id: "keyboard_shortcuts",
    title: "Keyboard Shortcuts",
    message: "Press ? to view available keyboard shortcuts for faster navigation.",
    priority: 10,
  },
} as const;

// Helper to get hint by ID
export const getHint = (id: string): HintDefinition | undefined => {
  return Object.values(HINTS).find((hint) => hint.id === id);
};

// Helper to get hints for a specific page
export const getHintsForPage = (page: string): HintDefinition[] => {
  return Object.values(HINTS)
    .filter((hint) => !hint.pages || hint.pages.includes(page))
    .sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99));
};

// Type for hint IDs
export type HintId = (typeof HINTS)[keyof typeof HINTS]["id"];
